import { ResizeHub } from './chunk-QXB7ANVF.js';
import { bindMethod, round, damp, clamp } from './chunk-QF7HVQZZ.js';
import { Raf } from './chunk-WILRAE77.js';
import './chunk-MRQ3HZFA.js';

// src/native-scroller.ts
var NativeScroller = class {
  constructor(config = {}) {
    this.routes = /* @__PURE__ */ Object.create(null);
    this.url = "/";
    this.current = 0;
    this.target = 0;
    this.min = 0;
    this.max = 0;
    this._isRunning = false;
    this.isPaused = false;
    this.listeners = /* @__PURE__ */ new Set();
    this.rqd = false;
    this._damping = config.damping ?? 0.09;
    this._preventNextNativeScrollEvent = false;
    this._resetVelocityTimeout = null;
    this.isTouching = false;
    this.touchStart = { x: 0, y: 0 };
    this.lastTouchPos = 0;
    this.wheelMultiplier = 1;
    this.touchMultiplier = 1;
    this.syncTouch = false;
    this.syncTouchLerp = 0.075;
    this.smoothWheel = true;
    this.keyboard = true;
    this._container = config.container ?? null;
    this._onUpdate = config.onUpdate ?? null;
    bindMethod(this, [
      "loop",
      "resize",
      "handleNativeScroll",
      "onWheel",
      "onTouchStart",
      "onTouchMove",
      "onTouchEnd",
      "onKeyDown"
    ]);
    this._boundScroll = this.handleNativeScroll.bind(this);
    this._boundWheel = this.onWheel.bind(this);
    this._boundTouchStart = this.onTouchStart.bind(this);
    this._boundTouchMove = this.onTouchMove.bind(this);
    this._boundTouchEnd = this.onTouchEnd.bind(this);
    this._boundKeyDown = this.onKeyDown.bind(this);
    this._listenersAttached = false;
    this._raf = new Raf("scroller", this.loop.bind(this));
    this.resizeId = ResizeHub.add(this.resize.bind(this));
  }
  /** Get whether scroller is currently running */
  get isRunning() {
    return this._isRunning;
  }
  /** Current route's scroll direction */
  get direction() {
    return this.routes[this.url]?.direction ?? "vertical";
  }
  /** Get/set damping factor (0-1) */
  get damping() {
    return this._damping;
  }
  set damping(value) {
    this._damping = Math.max(0, Math.min(1, value));
  }
  /**
   * Update configuration after construction
   */
  configure(config) {
    if (config.container !== void 0) this._container = config.container;
    if (config.onUpdate !== void 0) this._onUpdate = config.onUpdate;
    if (config.damping !== void 0) this._damping = config.damping;
  }
  /**
   * Set direction for the current route
   */
  setDirection(dir) {
    const state = this.routes[this.url];
    if (state) state.direction = dir;
    this.resize();
  }
  /**
   * Set the scroll container element
   */
  setContainer(el) {
    this._container = el;
  }
  /**
   * Subscribe to scroll updates
   */
  subscribe(fn) {
    if (typeof fn !== "function") return () => {
    };
    this.listeners.add(fn);
    try {
      fn(this.getState());
    } catch {
    }
    return () => {
      this.listeners.delete(fn);
    };
  }
  notify() {
    if (!this.listeners.size && !this._onUpdate) return;
    const ev = this.getState();
    if (this._onUpdate) {
      try {
        this._onUpdate(ev);
      } catch {
      }
    }
    for (const cb of this.listeners) {
      try {
        cb(ev);
      } catch {
      }
    }
  }
  /**
   * Register a route for scroll state tracking
   */
  registerRoute(url, state) {
    const normalizedUrl = url !== "/" ? url.replace(/\/$/, "") : "/";
    this.routes[normalizedUrl] = {
      current: state?.current ?? 0,
      target: state?.target ?? 0,
      direction: state?.direction ?? "vertical"
    };
  }
  /**
   * Initialize routes from a map of URLs
   */
  initRoutes(routes) {
    this.routes = /* @__PURE__ */ Object.create(null);
    const routeKeys = Array.isArray(routes) ? routes : Object.keys(routes);
    for (const routeKey of routeKeys) {
      this.registerRoute(routeKey);
    }
  }
  /**
   * Set the active route (call on navigation)
   */
  setActiveRoute(url) {
    const u = url ?? location.pathname;
    this.url = u !== "/" ? u.replace(/\/$/, "") : "/";
    const st = this.routes[this.url];
    if (!st) {
      this.routes[this.url] = {
        current: 0,
        target: 0,
        direction: "vertical"
      };
    }
    this.updateAll(0);
    this.resize();
  }
  /**
   * Resize handler - recalculates scroll bounds
   */
  resize() {
    const isH = this.direction === "horizontal";
    const viewport = isH ? window.innerWidth : window.innerHeight;
    let content;
    if (this._container) {
      content = isH ? this._container.scrollWidth : this._container.scrollHeight;
    } else {
      content = isH ? document.documentElement.scrollWidth : document.documentElement.scrollHeight;
    }
    this.max = Math.max(content - viewport, 0);
    this.max = round(this.max);
    const routeState = this.routes[this.url];
    if (!routeState) return;
    this.updateAll(this.clampValue(routeState.current || 0));
  }
  handleNativeScroll() {
    if (this._resetVelocityTimeout !== null) {
      clearTimeout(this._resetVelocityTimeout);
      this._resetVelocityTimeout = null;
    }
    if (this._preventNextNativeScrollEvent) {
      this._preventNextNativeScrollEvent = false;
      return;
    }
    const routeState = this.routes[this.url];
    if (!routeState) return;
    const isH = routeState.direction === "horizontal";
    const y = this.clampValue(isH ? window.scrollX || 0 : window.scrollY || 0);
    if (routeState.current !== y) {
      routeState.current = y;
      routeState.target = y;
      this.current = y;
      this.target = y;
      this.rqd = true;
      this.notify();
    }
  }
  setScroll(value) {
    this._preventNextNativeScrollEvent = true;
    const isH = this.direction === "horizontal";
    window.scrollTo({
      [isH ? "left" : "top"]: value,
      behavior: "instant"
    });
  }
  onWheel(event) {
    if (this.isPaused) return;
    if (event.ctrlKey) return;
    if (!this.smoothWheel) return;
    event.preventDefault();
    const isH = this.direction === "horizontal";
    let delta = isH ? event.deltaX !== 0 ? event.deltaX : event.deltaY : event.deltaY;
    if (event.deltaMode === 1) delta *= 40;
    if (event.deltaMode === 2) delta *= 800;
    delta *= this.wheelMultiplier;
    this.update(this.target + delta);
  }
  onTouchStart(event) {
    if (this.isPaused) return;
    const touch = event.touches[0];
    if (!touch) return;
    this.isTouching = true;
    this.touchStart = { x: touch.clientX, y: touch.clientY };
    const isH = this.direction === "horizontal";
    this.lastTouchPos = isH ? touch.clientX : touch.clientY;
  }
  onTouchMove(event) {
    if (!this.isTouching || this.isPaused) return;
    if (!this.syncTouch) return;
    const touch = event.touches[0];
    if (!touch) return;
    const isH = this.direction === "horizontal";
    const pos = isH ? touch.clientX : touch.clientY;
    const delta = (this.lastTouchPos - pos) * this.touchMultiplier;
    this.lastTouchPos = pos;
    const startPos = isH ? this.touchStart.x : this.touchStart.y;
    const totalDelta = Math.abs(pos - startPos);
    if (totalDelta > 5) {
      event.preventDefault();
      this.update(this.target + delta);
    }
  }
  onTouchEnd() {
    this.isTouching = false;
  }
  onKeyDown(event) {
    if (this.isPaused) return;
    if (!this.keyboard) return;
    const target = event.target;
    if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
      return;
    }
    let delta = 0;
    const isH = this.direction === "horizontal";
    const step = 100;
    const pageStep = (isH ? window.innerWidth : window.innerHeight) * 0.9;
    switch (event.key) {
      case "ArrowUp":
      case "ArrowLeft":
        delta = -step;
        break;
      case "ArrowDown":
      case "ArrowRight":
        delta = step;
        break;
      case "PageUp":
        delta = -pageStep;
        break;
      case "PageDown":
        delta = pageStep;
        break;
      case "Home":
        this.scrollTo(0, true);
        event.preventDefault();
        return;
      case "End":
        this.scrollTo(this.max, true);
        event.preventDefault();
        return;
      case " ":
        delta = event.shiftKey ? -pageStep : pageStep;
        break;
      default:
        return;
    }
    if (delta !== 0) {
      event.preventDefault();
      this.update(this.target + delta);
    }
  }
  loop() {
    this.rqd = false;
    const routeState = this.routes[this.url];
    if (!routeState) {
      this._raf.stop();
      this._isRunning = false;
      return;
    }
    const needsUpdate = Math.abs(routeState.current - routeState.target) > 0.5;
    if (needsUpdate && this._isRunning) {
      const effectiveDamping = this.isTouching ? this.syncTouchLerp : this._damping;
      this.current = routeState.current = damp(
        routeState.current,
        routeState.target,
        effectiveDamping
      );
      this.setScroll(round(this.current));
      this.rqd = true;
    } else if (Math.abs(routeState.current - routeState.target) <= 0.5 && routeState.current !== routeState.target) {
      this.current = routeState.current = routeState.target;
      this.setScroll(round(this.current));
    }
    if (this.rqd) this.notify();
  }
  /**
   * Update target scroll position
   */
  update(targetValue) {
    if (this.isPaused) return;
    const routeState = this.routes[this.url];
    if (!routeState) return;
    const clamped = this.clampValue(targetValue);
    routeState.target = clamped;
    this.target = clamped;
  }
  /**
   * Update both current and target scroll position (instant jump)
   */
  updateAll(value) {
    const routeState = this.routes[this.url];
    if (!routeState) return;
    const clamped = this.clampValue(value);
    routeState.target = clamped;
    routeState.current = clamped;
    this.current = clamped;
    this.target = clamped;
  }
  /**
   * Scroll to a specific position
   */
  scrollTo(position, immediate = false) {
    if (immediate) {
      this.updateAll(this.clampValue(position));
      this.setScroll(round(this.current));
    } else {
      this.update(position);
    }
  }
  /**
   * Scroll by a delta amount
   */
  scrollBy(delta) {
    this.update(this.target + delta);
  }
  clampValue(value) {
    return round(clamp(value, this.min, this.max));
  }
  /**
   * Pause scrolling — detaches all input listeners
   */
  pause() {
    this.isPaused = true;
    this._detachListeners();
  }
  /**
   * Resume scrolling — reattaches listeners
   */
  resume() {
    this.isPaused = false;
    if (this._isRunning) {
      this._attachListeners();
    }
  }
  _attachListeners() {
    if (this._listenersAttached) return;
    window.addEventListener("scroll", this._boundScroll, { passive: true });
    window.addEventListener("wheel", this._boundWheel, { passive: false });
    window.addEventListener("touchstart", this._boundTouchStart, { passive: true });
    window.addEventListener("touchmove", this._boundTouchMove, { passive: false });
    window.addEventListener("touchend", this._boundTouchEnd, { passive: true });
    if (this.keyboard) {
      window.addEventListener("keydown", this._boundKeyDown);
    }
    this._listenersAttached = true;
  }
  _detachListeners() {
    if (!this._listenersAttached) return;
    window.removeEventListener("scroll", this._boundScroll);
    window.removeEventListener("wheel", this._boundWheel);
    window.removeEventListener("touchstart", this._boundTouchStart);
    window.removeEventListener("touchmove", this._boundTouchMove);
    window.removeEventListener("touchend", this._boundTouchEnd);
    if (this.keyboard) {
      window.removeEventListener("keydown", this._boundKeyDown);
    }
    this._listenersAttached = false;
  }
  /**
   * Start the scroller (begin RAF loop and attach listeners)
   */
  start() {
    this._isRunning = true;
    this._attachListeners();
    this._raf.run();
  }
  /**
   * Stop the scroller
   */
  stop() {
    this._raf.stop();
    this._isRunning = false;
    this._detachListeners();
  }
  /**
   * Destroy the scroller and clean up
   */
  destroy() {
    this.stop();
    this.listeners.clear();
    ResizeHub.remove(this.resizeId);
    if (this._resetVelocityTimeout) {
      clearTimeout(this._resetVelocityTimeout);
    }
  }
  /**
   * Get current scroll state
   */
  getState() {
    const st = this.routes[this.url] ?? {
      direction: "vertical"
    };
    return {
      url: this.url,
      current: this.current,
      target: this.target,
      min: this.min,
      max: this.max,
      progress: this.max > 0 ? this.current / this.max : 0,
      mode: "native",
      direction: st.direction
    };
  }
};
var native_scroller_default = NativeScroller;

export { NativeScroller, native_scroller_default as default };
//# sourceMappingURL=native-scroller.js.map
//# sourceMappingURL=native-scroller.js.map